import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  FormControl,
  Select,
  Tooltip,
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import Logo from "../../assets/copysa.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setUserCredentials } from "../../redux/user/userSlice";
import Spinner from "../../components/Spinner";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import toast from "react-hot-toast";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import LanguageIcon from "@mui/icons-material/Language";
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browserName = "Unknown Browser";
  let osName = "Unknown OS";

  // Detect browser
  if (userAgent.indexOf("Firefox") > -1) {
    browserName = "Mozilla Firefox";
  } else if (
    userAgent.indexOf("Chrome") > -1 &&
    userAgent.indexOf("Edg") === -1
  ) {
    browserName = "Google Chrome";
  } else if (
    userAgent.indexOf("Safari") > -1 &&
    userAgent.indexOf("Chrome") === -1
  ) {
    browserName = "Safari";
  } else if (userAgent.indexOf("Edg") > -1) {
    browserName = "Microsoft Edge";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    browserName = "Opera";
  }

  // Detect OS
  if (userAgent.indexOf("Win") > -1) {
    osName = "Windows";
  } else if (userAgent.indexOf("Mac") > -1) {
    osName = "MacOS";
  } else if (userAgent.indexOf("Linux") > -1) {
    osName = "Linux";
  } else if (userAgent.indexOf("Android") > -1) {
    osName = "Android";
  } else if (userAgent.indexOf("like Mac") > -1) {
    osName = "iOS";
  }

  return { browserName, osName };
};

const Login = () => {
  const location = useLocation(); // Access the location object

  const [password, setPassword] = useState("");
  const phoneFromState = location.state?.phone || ""; // Get the phone from state or set to an empty string
  const [phone, setPhone] = useState(phoneFromState); // Set initial
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [language, setLanguage] = useState(i18n.language);
  const [anchorEl, setAnchorEl] = useState(null);

  const isRtl = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleInjectOtp = useCallback(
    (otpValue) => {
      setPassword(otpValue);
    },
    [setPassword]
  );
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "fa", label: "فارسی" },
  ];
  useEffect(() => {
    const onLanguageChanged = (lng) => setLanguage(lng);
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);
  const handleLangSelect = (lang) => {
    i18n.changeLanguage(lang);
    document.cookie = `i18next=${lang}; path=/; domain=.copysa.ir; max-age=31536000`;

    setAnchorEl(null);
  };
  useEffect(() => {
    window.injectOtp = handleInjectOtp;
    return () => {
      delete window.injectOtp; // Cleanup on unmount
    };
  }, [handleInjectOtp]);

  useEffect(() => {
    if ("OTPCredential" in window) {
      const abortController = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: abortController.signal,
        })
        .then((otp) => {
          if (otp && otp.code) {
            setPassword(otp.code); // Auto-fill the OTP in the password field
            setError(""); // Clear any previous error
          }
          abortController.abort(); // Stop listening after receiving OTP
        })
        .catch((err) => {
          abortController.abort(); // Stop listening
        });
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { browserName, osName } = getBrowserInfo();
    const loginData = {
      phone,
      password,
      operationsystem: osName,
      browser: browserName,
    };

    try {
      const response = await fetch("https://api.copysa.ir/api/User/Login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const iduser = data.value.idstruser;

        // Dispatch user credentials
        dispatch(setUserCredentials({ phone, password, iduser }));

        // Navigate to the home page
        navigate("/", { replace: true });
      } else {
        const errorText = await response.text(); // Get the response as text
        try {
          const errorData = JSON.parse(errorText); // Try to parse the response as JSON
          toast.error(errorData.message || t("Toast.wrongcred"));
          setError(errorData.message || t("Toast.wrongcred"));
        } catch (err) {
          // If response is not JSON, show the raw error text
          setError(errorText || t("Toast.err"));
        }
      }
    } catch (err) {
      // Handle network or unexpected errors
      toast.error(t("Toast.servererror"));
      setError(t("Toast.loginerr"));
    } finally {
      setLoading(false);
    }
  };
  const toggleLanguage = () => {
    const newLang = language === "fa" ? "en" : "fa";
    handleLangSelect(newLang);
  };
  return (
    <Box
      sx={{
        backgroundColor: "#F7F2FB",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        component="header"
        sx={{
          backgroundColor: "#512c63",
          width: "100%",
          paddingX: 2,
          paddingY: 3,
          position: "sticky",
          top: 0,
          minHeight: "250px",
          zIndex: 10,
        }}
      >
        {/* Content container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            height: "100%",
          }}
        >
          {/* Left: Language Selector */}
        <Box
  sx={{
    width: "100%",
    mb: 5,
    display: "flex",
    justifyContent: "flex-start",
  }}
  dir="rtl"
>
  <FormControl variant="outlined" size="small">
    <Select
      value={language}
      onChange={(e) => handleLangSelect(e.target.value)}
      sx={{
        color: "white",
        border: "1px solid white",
        borderRadius: 2,
        px: 2,
        backgroundColor: "rgba(255,255,255,0.08)",
        "& .MuiSvgIcon-root": { color: "white" },
        // Ensure the icon doesn't get cut off
        pl: "2.5rem", // Make space for the icon
      }}
      disableUnderline
      aria-label={t("aria.selectLanguage")}
      // 👇 This is the key: Add the icon as a start adornment
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ color: "white", mr: 0 }}>
            <TranslateIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    >
      {languageOptions.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          aria-label={t("aria.selectLanguage", { lang: option.label })}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {/* 👉 NO ICON in menu items — only in the select field */}
            <Typography>{option.label}</Typography>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>
          {/* Center: Logo */}
          <Box sx={{ textAlign: "center" }}>
            <img src={Logo} width={225} height={100} alt="copysa logo" />
          </Box>
          {/* Right: Placeholder for future elements */}
        </Box>

        {/* Wave Divider */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            lineHeight: 0,
            zIndex: -2,
          }}
        >
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{
              display: "block",
              width: "100%",
              height: "50px",
              transform: "rotateY(180deg)",
              marginBottom: "-5px",
            }}
          >
            <path
              d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
              fill="#F7F2FB"
            />
          </svg>
        </Box>
      </Box>
      {/* Main Content Section */}
      <Box
        component="main"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: "2",
        }}
      >
        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{
            color: "black",
            width: { xs: "100%", md: "70%" },
            display: "flex",
            flexDirection: "column",
            paddingX: "2rem",
          }}
        >
          <Typography
            textAlign="center"
            variant="h1"
            marginBottom={5}
            fontSize={28}
          >
            {t("Auth.login")}
          </Typography>

          {/* Phone Number Field */}
          <TextField
            required
            name="number"
            value={phone}
            helperText={error}
            error={!!error}
            onChange={(e) => setPhone(e.target.value)}
            id="number"
            label={t("Auth.Phone")}
            variant="outlined"
            fullWidth
            sx={{ marginBottom: "1.75rem" }}
            InputLabelProps={{
              required: false,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneEnabledIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              autoComplete: "tel",
            }}
          />

          {/* Password Field */}
          <TextField
            required
            type={showPassword ? "text" : "password"}
            value={password}
            name="otp" // Correct name for OTP
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            helperText={error}
            id="password"
            label={t("Auth.Password")}
            variant="outlined"
            autoComplete="one-time-code" // Correct autoComplete for OTP auto-fill
            sx={{ marginBottom: ".5rem" }}
            InputLabelProps={{ required: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />

          {/* Forgot Password Link */}
          <Typography variant="h5">
            <Link to="/auth/forgot" className="text-blue">
              {t("Auth.Forget")}
            </Link>
          </Typography>

          {/* ReCAPTCHA */}
        </Box>
      </Box>

      {/* Footer Section */}
      <Box
        component="footer"
        sx={{
          pb: "1rem",
          textAlign: "center",
          width: { xs: "86%", md: "30%" },
          marginX: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Submit Button */}
        <Button
          type="submit"
          onClick={handleLogin}
          disabled={loading}
          variant="contained"
          color="primary"
          sx={{
            width: "100%",
            borderRadius: "8px",
            my: "1rem",
            padding: "12px",
            fontSize: "16px",
          }}
          size="large"
        >
          {t("Auth.login")}
          {loading && <Spinner />}
        </Button>

        <Typography variant="h5" component="h1">
          {t("Auth.Noacc")}
          <Link to="/auth/signup" className="text-blue mx-1">
            {t("Auth.SignUp")}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
